import { coreText } from './tokenizer.js';
/** Maximum extension over chunkSize (rule c cap: at most +2 words). */
export const CHUNK_EXTENSION_CAP = 2;
export function stickSets(pack, lang) {
    const fold = (words) => new Set(words.map((w) => w.toLocaleLowerCase(lang)));
    return {
        forward: fold(pack?.stickForward ?? []),
        backward: fold(pack?.stickBackward ?? []),
    };
}
/**
 * Chunk table for a (doc, size, lang) triple — ratified rule set (Atlas
 * native-speaker review, 2026-07-02):
 *
 *  a. HARD BOUNDARY: a chunk never spans a token flagged endsSentence /
 *     endsParagraph / endsChapter. Fill closes the chunk AT that token; the
 *     next token starts a new chunk. All languages.
 *  b. Greedy fill to chunkSize within boundaries.
 *  c. EXTENSION (iterative): while the chunk tail ∈ stickForward OR the next
 *     token ∈ stickBackward, extend by one. Stops when neither rule
 *     triggers, at +2 words over chunkSize, or at a hard boundary.
 *     TIEBREAKER: the cap and hard boundaries always win over the stick
 *     rules — a chunk may close with a residual stick violation.
 *  d. Timing is unaffected: playback times each entry as the SUM of member
 *     intervals (PD-6) — never interval × chunkSize.
 *
 * Backward glue can never cross a sentence boundary: a boundary token is
 * always the last token of its chunk, so no stick rule is evaluated past it.
 */
export function buildChunkTable(tokens, chunkSize, sticks, lang) {
    const entries = [];
    if (chunkSize <= 1) {
        for (let i = 0; i < tokens.length; i++)
            entries.push({ start: i, count: 1 });
        return entries;
    }
    const cap = chunkSize + CHUNK_EXTENSION_CAP;
    const coreLower = (token) => coreText(token).toLocaleLowerCase(lang);
    let i = 0;
    while (i < tokens.length) {
        let count = 0;
        let closedByBoundary = false;
        // (b) greedy fill, closing at hard boundaries (a)
        while (count < chunkSize && i + count < tokens.length) {
            const token = tokens[i + count];
            count += 1;
            if (token !== undefined && isHardBoundary(token)) {
                closedByBoundary = true;
                break;
            }
        }
        // (c) iterative extension; cap and boundary win over stick rules
        while (!closedByBoundary && count < cap && i + count < tokens.length) {
            const tail = tokens[i + count - 1];
            const next = tokens[i + count];
            if (tail === undefined || next === undefined)
                break;
            const tailSticksForward = sticks.forward.has(coreLower(tail));
            const nextSticksBackward = sticks.backward.has(coreLower(next));
            if (!tailSticksForward && !nextSticksBackward)
                break;
            count += 1;
            if (isHardBoundary(next))
                break; // extension may end ON a boundary, never past it
        }
        entries.push({ start: i, count });
        i += count;
    }
    return entries;
}
function isHardBoundary(token) {
    return token.flags.endsSentence || token.flags.endsParagraph || token.flags.endsChapter;
}
//# sourceMappingURL=chunking.js.map